import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Users, MessagesSquare, MapPin, ArrowDownAZ, Map, Search, X, Plus, Scale, Gavel, ArrowRight, Lock, Star } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import api, { API } from "@/lib/api";
import { stateFromLocation } from "@/lib/states";
import { caseDisplayName } from "@/lib/caseName";

const photoUrl = (p) => (p ? p.external_url || (p.storage_path ? `${API}/photos/file/${p.storage_path}` : null) : null);
const coverOf = (c) => photoUrl(c.primary_photo) || c.thumbnail;

const CATEGORY_META = {
  active: { emoji: "🔥", name: "Active Cases" },
  missing: { emoji: "🧍", name: "Missing Persons" },
  unidentified: { emoji: "🦴", name: "Unidentified Human Remains" },
  cold: { emoji: "❄️", name: "Cold Cases" },
  wrongful: { emoji: "⚖️", name: "Wrongful Convictions" },
  dna: { emoji: "🧬", name: "DNA Cases" },
};

function SpaceCard({ c }) {
  const Icon = c.slug === "the-bullpen" ? Users
    : c.slug === "trial-strategy-workshop-tyler-robinson" ? Gavel
    : Scale;
  const cover = coverOf(c);
  return (
    <Link to={`/cases/${c.slug}`} data-testid={`space-card-${c.slug}`}
      className="group relative flex flex-col sm:flex-row rounded-md overflow-hidden border border-gold/30 bg-gradient-to-br from-gold/[0.08] to-charcoal hover:border-gold/60 transition-colors">
      {cover && (
        <div className="relative w-full sm:w-56 lg:w-64 shrink-0 h-28 sm:h-auto overflow-hidden">
          <img src={cover} alt="" className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-all duration-500" />
          <div className="absolute inset-0 bg-gradient-to-t sm:bg-gradient-to-r from-charcoal via-charcoal/40 to-transparent" />
          <span className="absolute top-3 left-3 label-mono bg-charcoal/80 backdrop-blur px-2 py-1 rounded-sm border border-gold/30 text-gold/90">{c.status}</span>
        </div>
      )}
      <div className="p-5 flex items-center gap-4 flex-1 min-w-0">
        <div className="shrink-0 w-11 h-11 rounded-sm bg-gold/15 border border-gold/30 flex items-center justify-center">
          <Icon className="w-5 h-5 text-gold" />
        </div>
        <div className="min-w-0 flex-1">
          {!cover && <div className="label-mono text-gold/80 mb-0.5">{c.status}</div>}
          <h3 className="font-display text-lg font-semibold text-white group-hover:text-gold transition-colors">{c.title}</h3>
          <p className="mt-1 text-sm text-white/50 leading-relaxed line-clamp-2">{c.summary}</p>
        </div>
        <span className="hidden sm:flex shrink-0 items-center gap-1 text-xs font-mono text-gold/70 group-hover:text-gold transition-colors">
          Enter <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
        </span>
      </div>
    </Link>
  );
}

export function CaseCard({ c }) {
  const meta = CATEGORY_META[c.category] || { emoji: "📁", name: c.category };
  const cover = coverOf(c);
  const { user } = useAuth();
  const isMember = user?.role === "admin" || user?.subscription_status === "active";
  const freeCaseId = user?.free_case_id;
  const locked = !!user && !isMember && c.case_id !== freeCaseId;
  const freeCase = !!user && !isMember && !!freeCaseId && c.case_id === freeCaseId;
  return (
    <Link to={`/cases/${c.slug}`} data-testid={`case-card-${c.slug}`}
      className="group flex flex-col sm:flex-row rounded-md overflow-hidden card-surface hover:border-gold/40 transition-colors">
      <div className="relative w-full sm:w-56 lg:w-64 shrink-0 h-32 sm:h-auto overflow-hidden">
        <img src={cover} alt="" className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-all duration-500" />
        <div className="absolute inset-0 bg-gradient-to-t sm:bg-gradient-to-r from-charcoal via-charcoal/40 to-transparent" />
        <span className="absolute top-3 left-3 label-mono bg-charcoal/80 backdrop-blur px-2 py-1 rounded-sm border border-white/10">{meta.emoji} {meta.name}</span>
        {locked && (
          <span className="absolute top-3 right-3 inline-flex items-center gap-1 text-[10px] font-mono px-2 py-1 rounded-sm bg-charcoal/80 backdrop-blur text-white/60 border border-white/15" data-testid={`case-locked-${c.slug}`}>
            <Lock className="w-3 h-3" /> Members
          </span>
        )}
        {freeCase && (
          <span className="absolute top-3 right-3 inline-flex items-center gap-1 text-[10px] font-mono px-2 py-1 rounded-sm bg-gold/90 text-charcoal font-bold" data-testid={`case-free-${c.slug}`}>
            <Star className="w-3 h-3" /> Your free case
          </span>
        )}
      </div>
      <div className="p-5 flex-1 min-w-0 flex flex-col justify-center">
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-display text-lg font-semibold text-white leading-tight group-hover:text-gold transition-colors">{caseDisplayName(c.title)}</h3>
          <span className="shrink-0 text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded-sm bg-gold/90 text-charcoal font-bold">{c.status}</span>
        </div>
        {c.location && <p className="mt-1.5 flex items-center gap-1.5 text-xs text-white/40 font-mono"><MapPin className="w-3 h-3" /> {c.location}</p>}
        <p className="mt-2 text-sm text-white/50 leading-relaxed line-clamp-2">{c.summary}</p>
        <div className="mt-3 flex items-center gap-4 text-xs text-white/40 font-mono">
          <span className="flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> {c.follower_count ?? 0}</span>
          <span className="flex items-center gap-1.5"><MessagesSquare className="w-3.5 h-3.5" /> {c.thread_count ?? 0}</span>
        </div>
      </div>
    </Link>
  );
}

export default function CasesPage() {
  const { user } = useAuth();
  const [cases, setCases] = useState([]);
  const [categories, setCategories] = useState([]);
  const [active, setActive] = useState(null);
  const [sort, setSort] = useState("az");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCategories(data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    api.get("/public/cases", { params: active ? { category: active } : {} })
      .then(({ data }) => setCases(data)).catch(() => {}).finally(() => setLoading(false));
  }, [active]);

  const q = query.trim().toLowerCase().replace(/^the\s+/i, "");
  const stripThe = (s) => (s || "").replace(/^the\s+/i, "");
  const spaces = cases.filter((c) => c.kind === "special" && c.slug !== "constitutional-corner");
  const regularCases = cases.filter((c) => c.kind !== "special");
  const filtered = q
    ? regularCases.filter((c) =>
        `${c.title || ""} ${c.location || ""} ${c.summary || ""} ${c.aliases || ""}`.toLowerCase().includes(q)
      )
    : regularCases;
  const sortedAZ = [...filtered].sort((a, b) => stripThe(a.title).localeCompare(stripThe(b.title)));
  const groupedByState = (() => {
    const groups = {};
    for (const c of sortedAZ) {
      const s = stateFromLocation(c.location);
      (groups[s] = groups[s] || []).push(c);
    }
    return Object.keys(groups)
      .sort((a, b) => (a === "Other" ? 1 : b === "Other" ? -1 : a.localeCompare(b)))
      .map((state) => ({ state, items: groups[state] }));
  })();

  return (
    <div className="min-h-screen bg-charcoal">
      <Helmet>
        <title>True-Crime Case Library — Help Solve The Case™</title>
        <meta name="description" content="Browse organized true-crime case workspaces: active cases, missing persons, cold cases, wrongful convictions and DNA cases. Timelines, court documents, videos and community — guided by a retired detective." />
        <link rel="canonical" href="https://helpsolvethecase.com/cases" />
        <meta property="og:title" content="True-Crime Case Library — Help Solve The Case™" />
        <meta property="og:description" content="Browse organized true-crime case workspaces guided by a retired detective. Educational & never accusatory." />
      </Helmet>
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <div className="label-mono mb-2">Case Library</div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold text-white">Choose a case to follow.</h1>
          </div>
          {user?.role === "admin" && (
            <Link to="/admin" data-testid="add-case-btn"
              className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2.5 bg-gold text-charcoal font-semibold rounded-sm hover:bg-gold-hover transition-colors text-sm">
              <Plus className="w-4 h-4" /> Add Case
            </Link>
          )}
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6">
          <div className="relative flex-1 max-w-xl">
            <Search className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search cases by name, location…"
              data-testid="cases-search-input"
              className="w-full bg-white/5 border border-white/10 rounded-sm pl-10 pr-9 py-2.5 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-gold/50 transition-colors"
            />
            {query && (
              <button onClick={() => setQuery("")} data-testid="cases-search-clear"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition-colors">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="flex items-center gap-1 rounded-sm border border-white/10 p-1 self-start sm:self-auto sm:ml-auto" data-testid="cases-sort-toggle">
            <button onClick={() => setSort("az")} data-testid="sort-az"
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-medium transition-colors ${sort === "az" ? "bg-gold text-charcoal" : "text-white/60 hover:text-white"}`}>
              <ArrowDownAZ className="w-3.5 h-3.5" /> A–Z
            </button>
            <button onClick={() => setSort("state")} data-testid="sort-state"
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-medium transition-colors ${sort === "state" ? "bg-gold text-charcoal" : "text-white/60 hover:text-white"}`}>
              <Map className="w-3.5 h-3.5" /> By State
            </button>
          </div>
        </div>

        {spaces.length > 0 && !q && !active && (
          <div className="mb-8" data-testid="member-spaces">
            <div className="label-mono mb-3 text-white/50">Member Spaces</div>
            <div className="grid grid-cols-1 gap-4">
              {spaces
                .slice()
                .sort((a, b) => {
                  const rank = (s) => s === "trial-strategy-workshop-tyler-robinson" ? 0 : s === "the-bullpen" ? 1 : 2;
                  return rank(a.slug) - rank(b.slug);
                })
                .map((c) => <SpaceCard key={c.case_id} c={c} />)}
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 mb-8">
          <button onClick={() => setActive(null)} data-testid="filter-all"
            className={`px-4 py-2 rounded-sm text-sm font-medium border transition-colors ${!active ? "bg-gold text-charcoal border-gold" : "border-white/10 text-white/60 hover:border-gold/40"}`}>
            All
          </button>
          {categories.map((c) => (
            <button key={c.id} onClick={() => setActive(c.id)} data-testid={`filter-${c.id}`}
              className={`px-4 py-2 rounded-sm text-sm font-medium border transition-colors ${active === c.id ? "bg-gold text-charcoal border-gold" : "border-white/10 text-white/60 hover:border-gold/40"}`}>
              {c.emoji} {c.name} <span className="opacity-60">{c.count}</span>
            </button>
          ))}
        </div>

        {loading ? (
          <div className="label-mono animate-pulse py-20 text-center">Loading cases…</div>
        ) : sortedAZ.length === 0 ? (
          <div className="card-surface rounded-md p-12 text-center text-white/40" data-testid="cases-empty">
            {q ? `No cases match “${query}”.` : "No cases in this category yet."}
          </div>
        ) : sort === "state" ? (
          <div className="space-y-10" data-testid="cases-by-state">
            {groupedByState.map((g) => (
              <div key={g.state} data-testid={`state-group-${g.state}`}>
                <div className="flex items-center gap-2 mb-4">
                  <MapPin className="w-4 h-4 text-gold" />
                  <h2 className="label-mono">{g.state}</h2>
                  <span className="text-xs text-white/30 font-mono">{g.items.length}</span>
                </div>
                <div className="grid grid-cols-1 gap-4">
                  {g.items.map((c) => <CaseCard key={c.case_id} c={c} />)}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {sortedAZ.map((c, i) => (
              <motion.div key={c.case_id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}>
                <CaseCard c={c} />
              </motion.div>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
